import { useEffect, useState } from "react";
import { X, Link2, CheckCircle2, AlertTriangle, Copy, Save, Trash2, Wand2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const VERTICALS = [
  { value: "", label: "— choose vertical —" },
  { value: "debt", label: "Debt Relief" },
  { value: "insurance", label: "Insurance" },
  { value: "medicare", label: "Medicare" },
  { value: "finance", label: "Finance" },
  { value: "legal", label: "Legal" },
  { value: "autos", label: "Autos" },
  { value: "health", label: "Health" },
  { value: "senior_living", label: "Senior Living" },
];

/**
 * Landing URL Builder modal.
 *
 * Lets the buyer paste a raw RSOC article URL and produces a fully
 * macro'd Facebook tracking URL (s1paid + FB dynamic params + UTMs +
 * forceKeys). Saves per-vertical templates for 1-click reuse from
 * Launch Campaign.
 *
 * Props:
 *   - open, onClose — modal control
 *   - onUse(url) — optional; called when the user clicks "Use this URL"
 *       (the Launch Campaign modal passes this to pre-fill its
 *       `landing_url` field).
 *   - initialVertical — (optional) pre-select vertical when opened from
 *       a vertical-scoped context (e.g. from Campaign Builder)
 */
export default function LandingUrlBuilder({ open, onClose, onUse, initialVertical }) {
  const [baseUrl, setBaseUrl] = useState("");
  const [vertical, setVertical] = useState(initialVertical || "");
  const [forceKeys, setForceKeys] = useState(["", "", "", "", "", ""]);
  const [built, setBuilt] = useState(null);
  const [busy, setBusy] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [saveName, setSaveName] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initialVertical) setVertical(initialVertical);
    api.get("/landing/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [open, initialVertical]);

  const build = async () => {
    if (!baseUrl.trim()) { toast.error("Paste a raw article URL first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/landing/build", {
        base_url: baseUrl.trim(),
        vertical,
        force_keys: forceKeys.filter(Boolean),
      });
      setBuilt(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
      setBuilt(null);
    } finally { setBusy(false); }
  };

  const copyUrl = () => {
    if (!built?.url) return;
    navigator.clipboard.writeText(built.url);
    toast.success("Tracking URL copied");
  };

  const saveTemplate = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("Name the template"); return; }
    if (!baseUrl.trim()) { toast.error("Paste a URL first"); return; }
    try {
      const { data } = await api.post("/landing/templates", {
        name, base_url: baseUrl.trim(), vertical,
        force_keys: forceKeys.filter(Boolean),
      });
      toast.success(data.overwritten ? `Template "${name}" overwritten` : `Template "${name}" saved`);
      setSaveName("");
      const { data: list } = await api.get("/landing/templates");
      setTemplates(list.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const loadTemplate = (t) => {
    setBaseUrl(t.base_url || "");
    setVertical(t.vertical || "");
    const fk = [...(t.force_keys || [])];
    while (fk.length < 6) fk.push("");
    setForceKeys(fk.slice(0, 6));
    setBuilt(null);
    toast.success(`Loaded "${t.name}"`);
  };

  const deleteTemplate = async (tpl_id, name) => {
    try {
      await api.delete(`/landing/templates/${tpl_id}`);
      setTemplates((l) => l.filter((t) => t.template_id !== tpl_id));
      toast.success(`Deleted "${name}"`);
    } catch { toast.error("Delete failed"); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="landing-url-builder-modal"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-[720px] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-indigo-600" />
              <h2 className="text-lg font-semibold tracking-tight">Landing URL Builder</h2>
            </div>
            <p className="text-[11px] text-slate-500 font-mono mt-0.5">
              Auto-appends `s1paid`, FB macros (`{`{campaign.id}`}` etc.), UTMs + `forceKeys` to any RSOC article URL
            </p>
          </div>
          <button onClick={onClose} data-testid="lub-close" className="text-slate-400 hover:text-slate-900">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Saved templates */}
          {templates.length > 0 && (
            <div data-testid="lub-templates" className="flex flex-wrap gap-1.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono w-full mb-0.5">
                Templates ({templates.length})
              </div>
              {templates.map((t) => (
                <div key={t.template_id} className="inline-flex items-center gap-0.5 bg-slate-100 border border-slate-200 rounded text-[11px] font-mono">
                  <button
                    onClick={() => loadTemplate(t)}
                    data-testid={`lub-load-${t.template_id}`}
                    className="px-2 py-1 hover:bg-slate-200 rounded-l"
                    title={t.base_url}
                  >
                    {t.name} {t.vertical && <span className="text-slate-400">· {t.vertical}</span>}
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.template_id, t.name)}
                    data-testid={`lub-del-${t.template_id}`}
                    className="px-1 py-1 hover:bg-red-100 hover:text-red-600 rounded-r"
                    title={`Delete "${t.name}"`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Inputs */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
              Raw RSOC article URL *
            </label>
            <input
              type="url"
              placeholder="https://search.yoursite.com/article/debt-relief"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              data-testid="lub-base-url"
              className="w-full mt-1 bg-white border border-slate-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Vertical</label>
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                data-testid="lub-vertical"
                className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-2 text-sm"
              >
                {VERTICALS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono" title="Save this config for 1-click reuse">
                Save as template
              </label>
              <div className="flex gap-1 mt-1">
                <input
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g. Debt Relief US"
                  data-testid="lub-tpl-name"
                  className="flex-1 bg-white border border-slate-200 rounded px-2 py-2 text-xs"
                />
                <button
                  onClick={saveTemplate}
                  disabled={!saveName.trim()}
                  data-testid="lub-save-tpl"
                  className="bg-slate-900 hover:bg-slate-700 disabled:opacity-40 text-white text-xs font-medium rounded px-3"
                  title="Save template"
                >
                  <Save className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
              forceKeys A–F <span className="text-slate-400 normal-case">(optional — seed S1 with specific keywords)</span>
            </label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {forceKeys.map((k, i) => (
                <input
                  key={i}
                  value={k}
                  onChange={(e) => {
                    const next = [...forceKeys];
                    next[i] = e.target.value;
                    setForceKeys(next);
                  }}
                  placeholder={`forceKey${String.fromCharCode(65 + i)}`}
                  data-testid={`lub-fk-${i}`}
                  className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
                />
              ))}
            </div>
          </div>

          <button
            onClick={build}
            disabled={busy || !baseUrl.trim()}
            data-testid="lub-build-btn"
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-medium rounded-md py-2.5 flex items-center justify-center gap-2"
          >
            <Wand2 className="w-4 h-4" /> {busy ? "Building…" : "Build tracking URL"}
          </button>

          {/* Result */}
          {built && (
            <div data-testid="lub-result" className="space-y-2">
              <div className="flex items-center gap-2">
                {built.validation.ok ? (
                  <span data-testid="lub-validation-ok" className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                    <CheckCircle2 className="w-3 h-3" /> Valid · {built.validation.param_count} params · {built.validation.macros_found.length} FB macros
                  </span>
                ) : (
                  <span data-testid="lub-validation-fail" className="inline-flex items-center gap-1 text-[11px] font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
                    <AlertTriangle className="w-3 h-3" /> Missing: {built.validation.missing.join(", ")}
                  </span>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-1">Final tracking URL</div>
                <div
                  data-testid="lub-final-url"
                  className="font-mono text-[11px] text-slate-900 break-all leading-relaxed"
                >
                  {built.url}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyUrl}
                  data-testid="lub-copy-btn"
                  className="flex items-center gap-1 bg-white border border-slate-200 hover:bg-slate-100 text-xs rounded-md px-3 py-1.5"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                {onUse && (
                  <button
                    onClick={() => { onUse(built.url); onClose(); }}
                    data-testid="lub-use-btn"
                    className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-md px-3 py-1.5"
                  >
                    Use this URL →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
